'use client';

import { useCallback, useMemo, useState } from 'react';

import { type PriorAuthSummaryT, usePatientPriorAuthsQuery } from '@/entities/prior-auth';
import { openPriorAuthActionOverlay } from '@/features/prior-auth-form';
import { formatRelativeTimeShort } from '@/shared/lib';
import { useCopilotOverlays } from '@/shared/stores/ui';
import { WorkspaceSection } from '@/shared/ui/primitives';

import { PriorAuthCaseDrawer } from './PriorAuthCaseDrawer';
import { PriorAuthsTable } from './PriorAuthsTable';

interface PatientPriorAuthsTabProps {
  userId: string;
}

/** The patient tracker and entry point into one current PA case. */
export function PatientPriorAuthsTab({ userId }: PatientPriorAuthsTabProps) {
  const query = usePatientPriorAuthsQuery(userId);
  const overlays = useCopilotOverlays();
  const [selectedSummary, setSelectedSummary] = useState<PriorAuthSummaryT | null>(null);

  const lastUpdatedAt = useMemo(() => {
    const timestamps = (query.data ?? []).flatMap(({ lastSeenAt }) => {
      const timestamp = lastSeenAt ? Date.parse(lastSeenAt) : Number.NaN;
      return Number.isFinite(timestamp) ? [timestamp] : [];
    });
    return timestamps.length > 0 ? Math.max(...timestamps) : null;
  }, [query.data]);

  const updateLabel = query.isFetching
    ? 'Updating…'
    : lastUpdatedAt === null
      ? null
      : `Updated ${formatRelativeTimeShort(lastUpdatedAt)}`;

  const handleSelect = useCallback((summary: PriorAuthSummaryT) => setSelectedSummary(summary), []);

  const handleOpenRequest = useCallback(
    (summary: PriorAuthSummaryT) => {
      openPriorAuthActionOverlay(overlays, {
        recordId: summary.id,
        patientUserId: userId,
        dosespotPrescriptionId: summary.dosespotPrescriptionId,
      });
    },
    [overlays, userId]
  );

  return (
    <div className="space-y-6" aria-label="Prior authorizations">
      <WorkspaceSection
        title="Prior authorizations"
        description="Insurance decisions and anything that needs your attention."
        action={
          updateLabel ? (
            <p className="text-xs text-muted-foreground" aria-live="polite">
              {updateLabel}
            </p>
          ) : null
        }
      >
        <PriorAuthsTable userId={userId} onSelect={handleSelect} />
      </WorkspaceSection>

      <PriorAuthCaseDrawer
        userId={userId}
        summary={selectedSummary}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedSummary(null);
          }
        }}
        onOpenRequest={handleOpenRequest}
      />
    </div>
  );
}
